import { LocationsSectionItem, WebMention } from 'src/common/models/common-types';
import {
    MediaSectionData,
    PostSectionData,
    ConnectionsSectionData,
    PersonalInformationSectionData,
} from './report-sections-data-types';
import moment from 'moment';
import _ from 'lodash';
import { InsightsSectionData, ProfileOverviewSectionData } from 'src/common/api/reportApi-types';
import { formatUrl } from 'src/common/utils/formatters';
import { formatInputFilters } from 'src/common/components/FiltersPanel/filter-utils';
import { Filter } from 'src/common/components/FiltersPanel/filter-types';
import { SearchInformation } from 'src/common/models/common-types';
import { SocialAccount } from './SocialAccount';

export class Report {
    constructor(
        public posts: PostSectionData = null,
        public media: MediaSectionData = null,
        public webMentions: WebMention[] = null,
        public summary: any = null,
        public locations: LocationsSectionItem[] = null,
        public connections: ConnectionsSectionData = null,
        public searchInformation: SearchInformation = null,
        public socialPresenceAccounts: SocialAccount[] = null,
        public personalInformation: PersonalInformationSectionData = null,
        public insights: InsightsSectionData = null,
    ) {}

    updateSection(sectionData: any) {
        switch (sectionData?.sectionName) {
            case 'webMentionsSection':
                sectionData.webMentions.map((webMention) => {
                    webMention.url = formatUrl(webMention.url);
                    return webMention;
                });
                this.webMentions = sectionData.webMentions;
                break;
            case 'locationSection':
                this.locations = sectionData.locations;
                break;
            case 'socialPresenceSection':
                sectionData.socialAccounts.map((socialAccount) => {
                    socialAccount.profileUrl = formatUrl(socialAccount.profileUrl);
                    return socialAccount;
                });
                this.socialPresenceAccounts = sectionData.socialAccounts;
                break;
            case 'personalInformationSection':
                this.personalInformation = sectionData.overview;
                break;
            case 'summarySection':
                this.summary = sectionData;
                this.searchInformation = sectionData.searchInformation;
                break;
            case 'connectionsSection':
                sectionData.connections.map((connection) => {
                    connection.profileUrl = formatUrl(connection.profileUrl);
                    return connection;
                });
                this.connections = sectionData;
                this.connections.filters = formatInputFilters(sectionData.filters, 'connections');
                break;
            case 'insightsSection':
                this.insights = sectionData;
                if (this.insights.mostFrequentMediaCategories) {
                    this.insights.mostFrequentMediaCategories = this.insights.mostFrequentMediaCategories.map(
                        (mediaCategory: any) => {
                            return {
                                category: mediaCategory.category,
                                mediaItems: mediaCategory.media.map((mediaItem) => ({
                                    ...mediaItem.media,
                                    account: mediaItem.account,
                                    tags: mediaItem.tags,
                                    owner: mediaItem.owner,
                                })),
                            };
                        },
                    );
                }
                if (this.insights.mostFrequentFaces) {
                    this.insights.mostFrequentFaces = this.insights.mostFrequentFaces.map((face: any) => {
                        face.images = face.images.map((image) => {
                            return {
                                ...image.media,
                                account: image.account,
                                owner: image.owner,
                                facePath: image.facePath,
                                source: image.source,
                            };
                        });
                        return face;
                    });
                }
                break;
            case 'postsSection':
                sectionData.posts.map((post) => {
                    post.url = formatUrl(post.url);
                    return post;
                });

                this.posts = {
                    filters: formatInputFilters(sectionData.filters, 'posts'),
                    amount: sectionData?.posts.length || 0,
                    posts: {
                        postsMappedByDate: sectionData.posts,
                        mostEngagingPosts: sectionData.posts,
                        overview: {
                            postsPublished: sectionData.bars,
                        },
                    },
                };
                break;
            case 'mediaSection':
                this.media = {
                    filters: formatInputFilters(sectionData.filters, 'media', overrideMediaFilters),
                    mediaItems: sectionData.media.map((mediaItem) => ({
                        ...mediaItem.media,
                        account: mediaItem.account,
                        tags: mediaItem.tags,
                        owner: mediaItem.owner,
                    })),
                };
                break;
        }
    }

    rebuildContentInsights(rawData) {
        const result = [];
        if (_.isArray(rawData)) {
            rawData?.forEach((content) => {
                if (content.clusterGroups?.length) {
                    result.push({
                        name: content.analysis,
                        contents: content.clusterGroups.map((content) => {
                            return {
                                title: `${content.groupLabel.value} (${content.groupImages.length})`,
                                dateRange:
                                    content.dateRange.oldestPostDate && content.dateRange.newestPostDate
                                        ? `${moment(content.dateRange.oldestPostDate).format('YYYY')} - ${moment(
                                              content.dateRange.newestPostDate,
                                          ).format('YYYY')}`
                                        : '',
                                hashTags: content.topTags.map((tag) => tag.value),
                                coverImage: {
                                    recognition: 'image',
                                    src: content.groupImage,
                                },
                                images: content.groupImages,
                            };
                        }),
                    });
                }
            });
        }
        return result;
    }
}

// HELPER FUNCTIONS

const overrideMediaFilters = (filterName: string): Partial<Filter> =>
    ['text', 'locationsExists'].includes(filterName) ? { selectionType: 'single', searchableOptions: false } : {};
